import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getKitDetail, toggleKitStatus } from '../config/api';
import { toast } from 'react-toastify';

const KitDetail = () => {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    loadKit();
  }, [kitId]);

  const loadKit = async () => {
    setLoading(true);
    try {
      const response = await getKitDetail(kitId);
      if (response && response.data && response.data.kit) {
        setKit(response.data.kit);
      }
    } catch (error) {
      toast.error('Failed to load kit details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      await toggleKitStatus(kit.id);
      toast.success('Kit status updated');
      loadKit();
    } catch (error) {
      toast.error('Failed to toggle kit status');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
          Loading kit details...
        </div>
      </div>
    );
  }

  if (!kit) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: '#6c757d', marginBottom: '16px' }}>Kit not found</p>
          <Link to="/kits" className="btn btn-primary">Back to Kits</Link>
        </div>
      </div>
    );
  }

  const mandatoryCategories = (kit.categories || []).filter(c => c.is_mandatory);
  const optionalCategories = (kit.categories || []).filter(c => !c.is_mandatory);
  const totalSkus = (kit.categories || []).reduce((sum, c) => sum + (c.skus || []).length, 0);

  return (
    <div className="container">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {kit.image && kit.image.startsWith('http') && (
            <img src={kit.image} alt={kit.name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #dee2e6' }} />
          )}
          <div>
            <div className="breadcrumb" style={{ marginBottom: '4px' }}>
              <Link to="/kits">Custom Kits</Link> / {kit.name}
            </div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {kit.name}
              <span style={{
                background: kit.active ? '#d1e7dd' : '#f8d7da',
                color: kit.active ? '#0f5132' : '#842029',
                padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
              }}>
                {kit.active ? 'Active' : 'Inactive'}
              </span>
            </h1>
            <p className="page-subtitle">
              {kit.warehouse ? `${kit.warehouse.name} — ${kit.warehouse.city}` : 'All Warehouses'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="btn"
            style={{ background: kit.active ? '#dc3545' : '#198754', color: 'white', padding: '8px 16px' }}
          >
            {toggling ? '...' : kit.active ? 'Deactivate' : 'Activate'}
          </button>
          <button onClick={() => navigate(`/kits/edit/${kit.id}?warehouseId=${kit.warehouse_id || 1}`)} className="btn btn-warning" style={{ padding: '8px 16px' }}>
            Edit Kit
          </button>
        </div>
      </div>

      {/* Description */}
      {kit.description && (
        <div className="card" style={{ padding: '14px 18px', background: '#f8f9fa', borderLeft: '3px solid #6c757d', marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Description</div>
          <p style={{ margin: 0, fontSize: '14px', color: '#343a40' }}>{kit.description}</p>
        </div>
      )}

      {/* Tags */}
      {kit.tags && (
        <div className="card" style={{ padding: '12px 18px', marginBottom: '16px', background: '#f8f9fa', borderLeft: '3px solid #0d6efd' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Tags</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {kit.tags.split(',').map((tag, idx) => (
              <span key={idx} style={{ background: '#e7f1ff', color: '#0c63e4', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
                {tag.trim()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Kit Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Kit Discount</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#198754' }}>{kit.discount_percent}%</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GST Rate</div>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{kit.gst_percent || '-'}%</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>HSN Code</div>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{kit.hsn_code || '-'}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Categories</div>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{(kit.categories || []).length}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total SKUs</div>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{totalSkus}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Min Optional</div>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{kit.min_optional_count}</div>
        </div>
      </div>

      {/* Variants */}
      {kit.variants && kit.variants.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px', color: '#6a1b9a' }}>
            Variants ({kit.variants.length})
          </h3>
          {kit.variants.map((variant, vIdx) => {
            const categoryMap = {};
            (kit.categories || []).forEach((c) => { categoryMap[String(c.category_id)] = c; });
            return (
              <div key={vIdx} style={{ marginBottom: '14px', padding: '14px', border: '1px solid #e1bee7', borderRadius: '8px', background: '#faf5ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{variant.label}</span>
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#6c757d', background: '#f3e5f5', padding: '2px 8px', borderRadius: '8px' }}>ID: {variant.id}</span>
                  </div>
                </div>

                {/* Qty overrides */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Quantity per Category</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {Object.entries(variant.categoryMinQty || {}).map(([catId, minQty]) => {
                      const cat = categoryMap[catId];
                      const maxQty = variant.categoryMaxQty?.[catId] || minQty;
                      return (
                        <span key={catId} style={{ background: 'white', border: '1px solid #ce93d8', padding: '4px 10px', borderRadius: '14px', fontSize: '12px' }}>
                          {cat?.category_name || `Cat ${catId}`}: {minQty}{maxQty !== minQty ? ` - ${maxQty}` : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Assigned products */}
                {variant.products && Object.keys(variant.products).length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Assigned Products</div>
                    {Object.entries(variant.products).map(([catId, productIds]) => {
                      if (!productIds || productIds.length === 0) return null;
                      const cat = categoryMap[catId];
                      const catSkus = cat?.skus || [];
                      return (
                        <div key={catId} style={{ marginBottom: '6px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#495057' }}>{cat?.category_name || `Cat ${catId}`}: </span>
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>
                            {productIds.map((pid) => {
                              const sku = catSkus.find((s) => s.product_id === pid);
                              return (
                                <span key={pid} style={{ background: '#d4edda', border: '1px solid #a3d9a5', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', color: '#155724', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                                  <span>{sku?.product?.name || `#${pid}`}</span>
                                  {sku?.product?.skuCode && <span style={{ fontSize: '10px', color: '#1a7a3a', fontFamily: 'monospace', opacity: 0.85 }}>{sku.product.skuCode}</span>}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mandatory Categories */}
      {mandatoryCategories.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px', color: '#1565c0' }}>
            Mandatory Categories ({mandatoryCategories.length})
          </h3>
          {mandatoryCategories.map((cat) => (
            <div key={cat.id} style={{ marginBottom: '14px', padding: '14px', border: '1px solid #bbdefb', borderRadius: '8px', background: '#f8fbff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{cat.category_name}</span>
                  {cat.label && (
                    <span style={{ fontSize: '12px', color: '#495057', background: '#e9ecef', padding: '2px 8px', borderRadius: '8px' }}>
                      {cat.label}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '12px', color: '#1565c0', background: '#e3f2fd', padding: '3px 10px', borderRadius: '10px' }}>
                  Qty: {cat.min_qty}{cat.max_qty !== cat.min_qty ? ` - ${cat.max_qty}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(cat.skus || []).map((sku) => (
                  <span key={sku.id} style={{ background: 'white', border: '1px solid #dee2e6', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                    <span>{sku.product ? sku.product.name : `Product #${sku.product_id}`}{sku.product?.wattage ? ` (${sku.product.wattage}W)` : ''}</span>
                    {sku.product?.skuCode && <span style={{ fontSize: '10px', color: '#6c757d', fontFamily: 'monospace' }}>{sku.product.skuCode}</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Optional Categories */}
      {optionalCategories.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px', color: '#e65100' }}>
            Optional Categories ({optionalCategories.length}) — Customer must pick at least {kit.min_optional_count}
          </h3>
          {optionalCategories.map((cat) => (
            <div key={cat.id} style={{ marginBottom: '14px', padding: '14px', border: '1px solid #ffe0b2', borderRadius: '8px', background: '#fffdf5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{cat.category_name}</span>
                  {cat.label && (
                    <span style={{ fontSize: '12px', color: '#495057', background: '#e9ecef', padding: '2px 8px', borderRadius: '8px' }}>
                      {cat.label}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '12px', color: '#e65100', background: '#fff3e0', padding: '3px 10px', borderRadius: '10px' }}>
                  Qty: {cat.min_qty}{cat.max_qty !== cat.min_qty ? ` - ${cat.max_qty}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(cat.skus || []).map((sku) => (
                  <span key={sku.id} style={{ background: 'white', border: '1px solid #dee2e6', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                    <span>{sku.product ? sku.product.name : `Product #${sku.product_id}`}{sku.product?.wattage ? ` (${sku.product.wattage}W)` : ''}</span>
                    {sku.product?.skuCode && <span style={{ fontSize: '10px', color: '#6c757d', fontFamily: 'monospace' }}>{sku.product.skuCode}</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitDetail;
